import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button, Row, Col, Card } from 'react-bootstrap';
import { authAPI } from '../utils/httpClient';
import styled from 'styled-components';
import { useI18n } from '../i18n';
import LanguageSwitcher from './common/LanguageSwitcher';
import CapCaptcha from './common/CapCaptcha';

// Import unified error handling components and hooks
import { useError } from '../hooks/useError';
import ErrorAlert from './ErrorAlert';
import { useNotification } from '../contexts/NotificationContext';
import { processApiError, isAuthError, getUserMessage, logError } from '../utils/apiErrorHandler';
import { getErrorMessage, mapErrorCodeToMessage, setErrorLanguage } from '../utils/errorMessages';

const LoginContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: var(--background-secondary);
`;

const LoginCard = styled(Card)`
  width: 100%;
  max-width: 450px;
  border-radius: 8px;
  box-shadow: var(--shadow);
  padding: 2rem;
`;

const LoginLogo = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  h1 {
    color: var(--primary);
    font-size: 2rem;
    font-weight: 500;
    margin-top: 1rem;
  }
`;

const Login = () => {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaConfig, setCaptchaConfig] = useState({ enabled: false });
  const [captchaResetSignal, setCaptchaResetSignal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Use unified error handling
  const { error, isError, showError, clearError, showValidationError, showAuthError, showNetworkError } = useError();
  const { showSuccess } = useNotification();

  // Keep error messages language in sync with global i18n
  useEffect(() => {
    setErrorLanguage(lang);
  }, [lang]);

  const resetCaptcha = useCallback(() => {
    setCaptchaToken('');
    setCaptchaResetSignal((current) => current + 1);
  }, []);

  const handleCaptchaConfig = useCallback((config) => {
    setCaptchaConfig(config);
  }, []);

  const handleCaptchaError = useCallback((error) => {
    const message = typeof error === 'string'
      ? error
      : getUserMessage(processApiError(error, 'captcha'), t('auth:errors.getCaptchaFailed'));
    showError(message, { context: 'captcha' });
  }, [showError, t]);

  const validateForm = () => {
    // Clear previous validation errors
    clearError();

    if (!username.trim()) {
      showValidationError(getErrorMessage('usernameRequired'), 'username');
      return false;
    }

    if (!password.trim()) {
      showValidationError(getErrorMessage('passwordRequired'), 'password');
      return false;
    }

    if (captchaConfig.enabled && !captchaToken) {
      showValidationError(t('auth:errors.captchaRequired'), 'captchaToken');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    clearError();

    try {
      const loginResponse = await authAPI.login(username.trim(), password, captchaToken);

      if (loginResponse.success) {
        // Login successful
        const userData = loginResponse.data.user;
        const tokens = loginResponse.data.tokens || {};
        const accessToken = tokens.access_token || '';
        const refreshToken = tokens.refresh_token || '';
        
        // Save authentication information (store real JWT when available)
        if (accessToken) {
          localStorage.setItem('token', accessToken);
          localStorage.setItem('authToken', accessToken); // keep legacy key for interceptor compatibility
        }
        if (refreshToken) {
          localStorage.setItem('refresh_token', refreshToken);
        }
        localStorage.setItem('userInfo', JSON.stringify(userData));
        localStorage.setItem('user', JSON.stringify(userData)); // Also save as 'user' for httpClient
        localStorage.setItem('organization', userData.organization || '');
        localStorage.setItem('orgCode', userData.org_code || '');
        
        // Show success notification
        showSuccess(getErrorMessage('success'), {
          title: 'Login Successful',
          delay: 2000
        });
        
        // Navigate to main page
        setTimeout(() => navigate('/choice'), 1000);
      } else {
        // Login failed - use backend error message
        const errorMessage = loginResponse.error?.message || getErrorMessage('loginFailed');
        const errorCode = loginResponse.error?.code;
        
        if (errorCode) {
          showAuthError(mapErrorCodeToMessage(errorCode, errorMessage));
        } else {
          showAuthError(errorMessage);
        }
        
        resetCaptcha();
      }
    } catch (error) {
      const processedError = processApiError(error, 'login');
      logError(processedError, { 
        username: username.trim(), // Safe to log username for debugging
        hasCaptchaToken: !!captchaToken
      });

      if (processedError.type === 'network') {
        showNetworkError(error);
      } else if (isAuthError(processedError)) {
        const message = mapErrorCodeToMessage(
          processedError.code, 
          getUserMessage(processedError, getErrorMessage('loginFailed'))
        );
        showAuthError(message);
      } else {
        const message = getUserMessage(processedError, getErrorMessage('loginFailed'));
        showError(message, { context: 'login_attempt' });
      }
      
      resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const switchToRegister = () => {
    navigate('/register');
  };

  return (
    <LoginContainer>
      <Container>
        <Row className="justify-content-center">
          <Col md={6}>
            <LoginCard>
              <div className="d-flex justify-content-end mb-2">
                <LanguageSwitcher size="sm" variant="outline-primary" />
              </div>
              <LoginLogo>
                <h1>{t('auth:login.title')}</h1>
              </LoginLogo>
              
              {/* Use unified error display component */}
              <ErrorAlert 
                error={error} 
                onDismiss={clearError} 
                showDetails={process.env.NODE_ENV === 'development'}
                className="mb-3"
              />
              
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('auth:login.username')}</Form.Label>
                  <Form.Control
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder={t('auth:login.usernamePlaceholder')}
                    disabled={loading}
                    isInvalid={isError && error?.details?.field === 'username'}
                  />
                  {isError && error?.details?.field === 'username' && (
                    <Form.Control.Feedback type="invalid">
                      {error.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('auth:login.password')}</Form.Label>
                  <Form.Control
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth:login.passwordPlaceholder')}
                    disabled={loading}
                    isInvalid={isError && error?.details?.field === 'password'}
                  />
                  {isError && error?.details?.field === 'password' && (
                    <Form.Control.Feedback type="invalid">
                      {error.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-3" style={{ display: captchaConfig.enabled ? undefined : 'none' }}>
                  <CapCaptcha
                    resetSignal={captchaResetSignal}
                    onTokenChange={setCaptchaToken}
                    onConfigChange={handleCaptchaConfig}
                    onError={handleCaptchaError}
                    lang={lang}
                  />
                  {isError && ['captchaToken', 'captcha_token'].includes(error?.details?.field) && (
                    <div className="invalid-feedback d-block">
                      {error.message}
                    </div>
                  )}
                </Form.Group>

                <div className="d-grid gap-2">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading}
                    size="lg"
                  >
                    {loading ? getErrorMessage('loading') : t('auth:login.submit')}
                  </Button>
                </div>

                <div className="text-center mt-3">
                  <Button variant="link" onClick={switchToRegister}>
                    {t('auth:login.registerLink')}
                  </Button>
                </div>
              </Form>
            </LoginCard>
          </Col>
        </Row>
      </Container>
    </LoginContainer>
  );
};

export default Login;
